import { PAID_BY_PMS } from '@/api/type/payment';
import { formatDateDisplay } from '@/lib/dates';

export const MONTHLY_FEE = 100;
export const PLAN_MONTHS = 100;

export type MonthYear = {
  month: number; // 0 to 11
  year: number; // e.g. 2026
};

export const DISPLAY_MONTH_NAMES = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
] as const;

export const getCurrentMonthYear = (): MonthYear => {
  const now = new Date();
  return {
    month: now.getMonth(),
    year: now.getFullYear(),
  };
};

/** Due month sent to the API as 'YYYY-MM', e.g. Oct 2026 → '2026-10'. */
export const formatMonthYearPayload = (my: MonthYear): string =>
  `${my.year}-${String(my.month + 1).padStart(2, '0')}`;

/** A saved due month for display: '2026-10' → 'Oct 2026'. Older payments saved as 'oct 2026' also show as 'Oct 2026'. */
export const formatDueMonth = (value: string): string => {
  const key = value.match(/^(\d{4})-(\d{2})/);
  if (key) return `${DISPLAY_MONTH_NAMES[Number(key[2]) - 1]} ${key[1]}`;
  return value.replace(/^\w/, (letter) => letter.toUpperCase());
};

/** The months a payment covers, e.g. 'Oct 2026 – Dec 2026', or one month. */
export const formatPaymentPeriod = (payment: { dueStart?: string; dueEnd?: string; month: string; year: number }): string => {
  if (!payment.dueStart) return `${payment.month} ${payment.year}`;
  const start = formatDueMonth(payment.dueStart);
  return payment.dueEnd && payment.dueEnd !== payment.dueStart ? `${start} – ${formatDueMonth(payment.dueEnd)}` : start;
};

export const formatMonthYearDisplay = (my: MonthYear): string => {
  return `${DISPLAY_MONTH_NAMES[my.month]} ${my.year}`;
};

export const calculateDuePeriod = (start: MonthYear, end: MonthYear): number => {
  const months = (end.year - start.year) * 12 + (end.month - start.month) + 1;
  return Math.max(1, months);
};

export const addMonthsToMonthYear = (start: MonthYear, monthsToAdd: number): MonthYear => {
  const totalMonths = start.year * 12 + start.month + monthsToAdd;
  const year = Math.floor(totalMonths / 12);
  const month = ((totalMonths % 12) + 12) % 12;
  return { month, year };
};

export const compareMonthYear = (a: MonthYear, b: MonthYear): number => {
  if (a.year !== b.year) return a.year - b.year;
  return a.month - b.month;
};

export const isSameMonthYear = (a: MonthYear, b: MonthYear): boolean => {
  return a.month === b.month && a.year === b.year;
};

export const isMonthYearInRange = (target: MonthYear, start: MonthYear, end: MonthYear): boolean => {
  return compareMonthYear(target, start) >= 0 && compareMonthYear(target, end) <= 0;
};

const MONTH_NAMES = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
] as const;

export const formatPaymentDate = (dateString: string | null | undefined): string =>
  formatDateDisplay(dateString);

export const getMonthYearFromDate = (dateString: string) => {
  const date = new Date(dateString);
  return {
    month: MONTH_NAMES[date.getMonth()],
    year: date.getFullYear(),
    monthIndex: date.getMonth() + 1,
  };
};

export const formatCurrency = (amount: number): string =>
  `₹${amount.toLocaleString('en-IN')}`;

const PAYMENT_TYPE_LABEL: Record<string, string> = { PMS: 'Paid by PMS' };

/** Display text for a payment type: the API sends "PMS", screens show "Paid by PMS". */
export const paymentTypeLabel = (type?: string | null): string => (type ? PAYMENT_TYPE_LABEL[type] ?? type : '');

/** True when a payment was Paid by PMS in the current calendar month. */
export const isPaidByPmsThisMonth = (payment?: { date: string; type: string } | null): boolean => {
  if (!payment || payment.type !== PAID_BY_PMS) return false;
  const date = new Date(payment.date);
  const now = new Date();
  return date.getMonth() === now.getMonth() && date.getFullYear() === now.getFullYear();
};
